<template>
  <!-- ════════════════════════════════════════════════════════════════
       RecommendedArticles.vue
       ສະແດງບົດຄວາມລ່າສຸດ 3 ອັນ — ໃຊ້ໃນ HomePage.vue
  ════════════════════════════════════════════════════════════════ -->
  <section class="recommended-articles">
    <!-- Section Header -->
    <div class="section-header">
      <div class="title-wrapper">
        <h2 class="section-title">ບົດຄວາມທີ່ໜ້າສົນໃຈ</h2>
        <div class="title-underline"></div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="skeleton-grid">
        <div v-for="n in 3" :key="n" class="skeleton-card">
          <div class="skeleton-image"></div>
          <div class="skeleton-body">
            <div class="skeleton-line badge"></div>
            <div class="skeleton-line title"></div>
            <div class="skeleton-line title short"></div>
            <div class="skeleton-line excerpt"></div>
            <div class="skeleton-footer"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <p>ອຸ້ຍ! ເກີດຂໍ້ຜິດພາດ: {{ error.message }}</p>
    </div>

    <!-- Cards -->
    <template v-else>
      <div class="articles-grid">
        <article
          v-for="article in latestArticles"
          :key="article.id"
          class="article-card"
          @click="goToDetail(article.id)"
        >
          <!-- Thumbnail -->
          <div class="card-image-wrapper">
            <img
              :src="article.cardImageCover"
              :alt="article.title"
              class="card-image"
              loading="lazy"
            />
            <span class="category-badge">{{ article.category }}</span>
          </div>

          <!-- Body -->
          <div class="card-body">
            <h3 class="card-title">{{ article.title }}</h3>
            <p class="card-excerpt">{{ article.excerpt }}</p>

            <!-- Footer -->
            <div class="card-footer">
              <div class="card-meta">
                <!-- Clock icon -->
                <span class="meta-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 -960 960 960"
                  >
                    <path
                      d="M339.5-108.5q-65.5-28.5-114-77t-77-114Q120-365 120-440t28.5-140.5q28.5-65.5 77-114t114-77Q405-800 480-800t140.5 28.5q65.5 28.5 114 77t77 114Q840-515 840-440t-28.5 140.5q-28.5 65.5-77 114t-114 77Q555-80 480-80t-140.5-28.5ZM480-440Zm112 168 56-56-128-128v-184h-80v216l152 152ZM224-866l56 56-170 170-56-56 170-170Zm512 0 170 170-56 56-170-170 56-56ZM480-160q117 0 198.5-81.5T760-440q0-117-81.5-198.5T480-720q-117 0-198.5 81.5T200-440q0 117 81.5 198.5T480-160Z"
                    />
                  </svg>
                </span>
                <span class="meta-text">{{ article.readTime }}</span>
              </div>

              <button
                class="read-more-btn"
                @click.stop="goToDetail(article.id)"
              >
                ອ່ານເພີ່ມເຕີມ
                <span class="arrow-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 -960 960 960"
                  >
                    <path
                      d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </article>
      </div>

      <!-- ปุ่มไปหน้า Articles ทั้งหมด -->
      <div class="view-all-wrapper">
        <button class="view-all-btn" @click="goToArticles">
          ບົດຄວາມທັງໝົດ
          <span class="arrow-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960">
              <path
                d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z"
              />
            </svg>
          </span>
        </button>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useArticles } from "@/data/useArticles";

const router = useRouter();

const { articles, loading, error } = useArticles();

// ดึง 3 บทความล่าสุด (query sort โดย date desc อยู่แล้ว → slice แรก 3 อัน)
const latestArticles = computed(() => articles.value.slice(0, 3));

function goToDetail(id) {
  router.push({ name: "ArticleDetail", params: { id } });
}

function goToArticles() {
  router.push({ name: "Articles" });
}
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Noto+Sans+Lao:wght@300;400;500;600;700;800&display=swap");

/* ─── Section Wrapper ───────────────────────────────────────────────────── */
.recommended-articles {
  font-family: "Noto Sans Lao", "Phetsarath OT", sans-serif;
  padding: 5rem 1.5rem;
  width: 100%;
  box-sizing: border-box;
}

/* ─── Section Header ────────────────────────────────────────────────────── */
.section-header {
  text-align: center;
  margin-bottom: 3.5rem;
}

.section-title {
  font-size: clamp(1.6rem, 3.5vw, 2.2rem);
  font-weight: 800;
  color: #344873;
  margin: 0 0 0.75rem;
  letter-spacing: -0.01em;
}
.title-wrapper {
  display: inline-block;
}
.title-underline {
  width: 100%;
  height: 0.25rem;
  background: #ea2127;
  border-radius: 99px;
  margin: 0 auto;
}

/* ─── Grid ──────────────────────────────────────────────────────────────── */
.articles-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.75rem;
  max-width: 72rem;
  margin: 0 auto;
}

/* ─── Card ──────────────────────────────────────────────────────────────── */
.article-card {
  background: #ffffff;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(52, 72, 115, 0.08);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.article-card:hover {
  transform: translateY(-0.75rem);
  box-shadow: 0 12px 40px rgba(52, 72, 115, 0.18);
}

/* ─── Card Image ────────────────────────────────────────────────────────── */
.card-image-wrapper {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 9;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
  background: #e2e8f0;
}

.article-card:hover .card-image {
  transform: scale(1.05);
}

.category-badge {
  position: absolute;
  top: 0.875rem;
  left: 0.875rem;
  background: #ea2127;
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  letter-spacing: 0.04em;
}

/* ─── Card Body ─────────────────────────────────────────────────────────── */
.card-body {
  padding: 1.375rem 1.375rem 1.125rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
  color: #344873;
  line-height: 1.55;
  margin: 0 0 0.625rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-excerpt {
  font-size: 0.875rem;
  color: #6b7a99;
  line-height: 1.65;
  margin: 0 0 1rem;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ─── Card Footer ───────────────────────────────────────────────────────── */
.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.875rem;
  border-top: 1px solid #e2e8f0;
}

.card-meta {
  display: flex;
  align-items: center;
  gap: 5px;
}

.meta-icon {
  width: 1.1rem;
  height: 1.1rem;
  fill: #6b7a99;
  flex-shrink: 0;
}

.meta-text {
  font-size: 0.78rem;
  color: #6b7a99;
}

.read-more-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  color: #344873;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.25s ease;
}

.read-more-btn:hover {
  color: #ea2127;
}

.arrow-icon {
  width: 1.1rem;
  height: 1.1rem;
  fill: currentColor;
  flex-shrink: 0;
  transition: transform 0.25s ease;
}

.read-more-btn:hover .arrow-icon {
  transform: translateX(3px);
}

/* ─── View All Button ───────────────────────────────────────────────────── */
.view-all-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 3rem;
}

.view-all-btn {
  background: #ea2127;
  color: #ffffff;
  border: none;
  padding: 0.875rem 2.5rem;
  border-radius: 50px;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
  letter-spacing: 0.02em;
}

.view-all-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.625rem 0.9375rem -0.1875rem rgba(0, 0, 0, 0.12);
}

.view-all-btn .arrow-icon {
  width: 1.2rem;
  height: 1.2rem;
  fill: #ffffff;
}

/* ─── Loading Skeleton ──────────────────────────────────────────────────── */
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.75rem;
  max-width: 72rem;
  margin: 0 auto;
}

.skeleton-card {
  background: #fff;
  border-radius: 1rem;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.skeleton-image {
  aspect-ratio: 16 / 9;
  background: linear-gradient(90deg, #e8eaed 25%, #f4f5f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

.skeleton-body {
  padding: 1.375rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.skeleton-line {
  height: 0.875rem;
  border-radius: 0.5rem;
  background: linear-gradient(90deg, #e8eaed 25%, #f4f5f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

.skeleton-line.badge {
  width: 35%;
  height: 1.3rem;
  border-radius: 20px;
}
.skeleton-line.title {
  width: 90%;
}
.skeleton-line.short {
  width: 60%;
}
.skeleton-line.excerpt {
  width: 75%;
}

.skeleton-footer {
  height: 2rem;
  margin-top: 0.5rem;
  border-radius: 0.5rem;
  background: linear-gradient(90deg, #e8eaed 25%, #f4f5f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ─── Error ─────────────────────────────────────────────────────────────── */
.error-container {
  text-align: center;
  color: #6b7a99;
  padding: 3rem;
  font-size: 1rem;
}

/* ─── Responsive ────────────────────────────────────────────────────────── */
@media (max-width: 64rem) {
  .articles-grid,
  .skeleton-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 40rem) {
  .recommended-articles {
    padding: 2.5rem 1rem 3rem;
  }

  .articles-grid,
  .skeleton-grid {
    grid-template-columns: 1fr;
    max-width: 28rem;
  }
}
</style>